import type {
  GuidedThread,
  Talk,
  TalkReflection,
} from '../../data/schema';
import type { StudyStore } from '../../services/studyStore';

interface ReflectionPanelOptions {
  talk: Talk;
  reflection: TalkReflection;
  activeThread?: GuidedThread;
  studyStore: StudyStore;
}

function routerLink(
  href: string,
  label: string,
  className?: string,
): HTMLAnchorElement {
  const link = document.createElement('a');
  link.href = href;
  link.dataset.router = '';
  link.textContent = label;
  if (className) link.className = className;
  return link;
}

export function createReflectionPanel(
  options: ReflectionPanelOptions,
): HTMLElement {
  const { talk, reflection, activeThread, studyStore } = options;
  const panel = document.createElement('section');
  panel.className = 'reflection-panel';
  panel.setAttribute('aria-labelledby', 'reflection-heading');
  const eyebrow = document.createElement('p');
  eyebrow.className = 'eyebrow eyebrow--gold';
  eyebrow.textContent = 'Personal study';
  const heading = document.createElement('h2');
  heading.id = 'reflection-heading';
  heading.textContent = 'Reflect on This Talk';
  const questions = document.createElement('ol');
  questions.className = 'reflection-questions';
  for (const question of reflection.questions) {
    const item = document.createElement('li');
    const copy = document.createElement('p');
    copy.textContent = question.question;
    const grounding = document.createElement('div');
    grounding.className = 'reflection-grounding';
    for (const [index, paragraphId] of question.groundedIn.entries()) {
      const suffix = activeThread
        ? `?thread=${encodeURIComponent(activeThread.threadId)}`
        : '';
      grounding.append(
        routerLink(
          `/read/${talk.talkId}/${paragraphId}${suffix}`,
          `Revisit grounding paragraph ${String(index + 1)}`,
          'text-link',
        ),
      );
    }
    item.append(copy, grounding);
    questions.append(item);
  }

  const nextHeading = document.createElement('h3');
  nextHeading.textContent = 'Read Next';
  const suggestions = document.createElement('div');
  suggestions.className = 'reflection-next';
  for (const suggestion of reflection.readNext) {
    const card = document.createElement('article');
    card.className = 'reflection-next__card';
    const speaker = document.createElement('p');
    speaker.textContent = suggestion.speaker;
    const title = document.createElement('h4');
    title.append(
      routerLink(`/read/${suggestion.talkId}`, suggestion.title),
    );
    const reason = document.createElement('p');
    reason.textContent = suggestion.reason;
    card.append(speaker, title, reason);
    suggestions.append(card);
  }

  const actions = document.createElement('div');
  actions.className = 'reflection-actions';
  const complete = document.createElement('button');
  complete.type = 'button';
  complete.className = 'button';
  complete.textContent = 'Mark talk complete';
  const status = document.createElement('p');
  status.className = 'inline-status';
  status.setAttribute('role', 'status');
  complete.addEventListener('click', () => {
    complete.disabled = true;
    void (async () => {
      try {
        const finalParagraph =
          [...talk.paragraphs]
            .reverse()
            .find((paragraph) => paragraph.kind === 'paragraph') ??
          talk.paragraphs.at(-1);
        await studyStore.saveProgress({
          talkId: talk.talkId,
          paragraphId: finalParagraph?.id ?? null,
          percent: 100,
          completed: true,
        });
        if (activeThread) {
          const allProgress = await studyStore.loadThreadProgress(
            talk.conferenceId,
          );
          const saved = allProgress.find(
            (progress) => progress.threadId === activeThread.threadId,
          );
          const completedTalkIds = new Set(saved?.completedTalkIds ?? []);
          completedTalkIds.add(talk.talkId);
          const threadComplete = activeThread.steps.every((step) =>
            completedTalkIds.has(step.talkId),
          );
          await studyStore.saveThreadProgress(
            talk.conferenceId,
            activeThread.threadId,
            [...completedTalkIds],
            saved?.completed === true || threadComplete,
          );
          status.textContent = threadComplete
            ? 'Talk and thread completion saved.'
            : 'Talk completion saved. Your active thread is updated.';
        } else {
          status.textContent = 'Talk completion saved.';
        }
        complete.textContent = 'Completed';
      } catch {
        complete.disabled = false;
        status.textContent =
          'Sign in or reconnect to save completion.';
      }
    })();
  });
  actions.append(complete, status);
  panel.append(
    eyebrow,
    heading,
    questions,
    nextHeading,
    suggestions,
    actions,
  );
  return panel;
}
